/**
 * Projected stat lines from fitted positional curves (§4.5).
 *
 * With Fantasy Nerds deferred (D-2), v1 has no projection API, so this is the
 * default and only shipped projection path. It works like this:
 *
 *   position rank in the active ranking source
 *     → evaluate the fitted stat curves for that position at that rank
 *     → a projected stat line (yards, receptions, TDs, made FGs by distance…)
 *     → score that line with the *user's own* `scoring_settings`
 *
 * The indirection through a stat line rather than through points is the whole
 * point: it is what makes FR-21 real. A curve of half-PPR points would misprice
 * a PPR, TE-premium, or superflex league; a curve of receptions reprices itself.
 *
 * ## What this can and cannot do
 *
 * It reproduces the *shape* of each positional curve and therefore most of the
 * BeerSheets-style value signal, which depends on shape and on where the
 * replacement baseline sits — not on per-player accuracy. What it structurally
 * cannot do is disagree with the rankings: if the active source has a player
 * 40th at his position, he gets roughly the 40th-best stat line at that
 * position. The engine can never say "the market is wrong about this guy on a
 * points basis." That is what a real projection source buys, and it is the
 * argument for revisiting D-2 in v2.
 *
 * Everything here is labelled `VOR (est.)` in the UI, and `Projection.estimated`
 * is true, so an estimate is never presented as a projection.
 *
 * Pure module — no I/O.
 */

import type { LeagueConfig, PlayerId, Position, Projection, StatLine } from './types.ts';
import { POSITION_CURVES, type StatCurve } from './data/positionCurves.ts';
import { scoreStatLine } from './scoring.ts';

export const CURVE_SOURCE_ID = 'curve-fit';

export function evaluateCurve(curve: StatCurve, rank: number): number {
  return curve.L / (1 + Math.pow(Math.max(rank, 0.001) / curve.r0, curve.k));
}

/**
 * The projected stat line for the player at `positionRank` of `position`.
 * Ranks past the fitted depth clamp to the depth — the tail is flat there, and
 * extrapolating a fitted curve into ranks nobody drafts from would be inventing
 * numbers.
 */
export function projectedStatLine(position: Position, positionRank: number): StatLine {
  const curve = POSITION_CURVES[position];
  if (!curve) return {};
  const rank = Math.min(Math.max(positionRank, 1), curve.depth);

  const line: StatLine = {};
  for (const [statKey, statCurve] of Object.entries(curve.stats)) {
    const value = evaluateCurve(statCurve, rank);
    if (Math.abs(value) >= 0.005) line[statKey] = Math.round(value * 100) / 100;
  }
  return line;
}

/**
 * Fill in aggregate scoring keys a league uses but the curve data expresses
 * granularly, and vice versa.
 *
 * Leagues are inconsistent about kicking in particular: some score `fgm_50p`,
 * some `fgm_50_59` plus `fgm_60p`, some just `fgm`. Without this step a league
 * on the aggregate keys would score every kicker at zero field goals — silently,
 * which is the worst kind of wrong. Keys already present are never overwritten,
 * so nothing is ever double-counted.
 */
export function expandStatLine(scoring: Record<string, number>, line: StatLine): StatLine {
  const out: StatLine = { ...line };
  const has = (k: string) => out[k] !== undefined;
  const sum = (...keys: string[]) =>
    keys.reduce((acc, k) => acc + (out[k] ?? 0), 0);

  if (scoring['fgm_50p'] !== undefined && !has('fgm_50p')) {
    out['fgm_50p'] = sum('fgm_50_59', 'fgm_60p');
  }
  if (scoring['fgm'] !== undefined && !has('fgm')) {
    out['fgm'] = sum('fgm_0_19', 'fgm_20_29', 'fgm_30_39', 'fgm_40_49', 'fgm_50_59', 'fgm_60p', 'fgm_50p');
  }
  if (scoring['fgm_yds'] !== undefined && !has('fgm_yds')) {
    // Approximate total FG distance from the bucket midpoints.
    out['fgm_yds'] =
      (out['fgm_0_19'] ?? 0) * 15 +
      (out['fgm_20_29'] ?? 0) * 25 +
      (out['fgm_30_39'] ?? 0) * 35 +
      (out['fgm_40_49'] ?? 0) * 45 +
      (out['fgm_50_59'] ?? 0) * 54 +
      (out['fgm_60p'] ?? 0) * 62;
  }
  if (scoring['pass_inc'] !== undefined && !has('pass_inc')) {
    out['pass_inc'] = Math.max(0, (out['pass_att'] ?? 0) - (out['pass_cmp'] ?? 0));
  }
  return out;
}

export interface CurveProjectionInput {
  player_key: PlayerId;
  position: Position;
  /** 1-based rank within the position in the active ranking source. */
  position_rank: number;
}

/**
 * Project a whole pool of players in one pass.
 *
 * Callers supply position ranks derived from whichever ranking source is
 * active, which is how the projection "inherits its ranking source's opinion"
 * (§4.5) rather than having an opinion of its own.
 */
export function projectFromCurves(
  players: CurveProjectionInput[],
  league: LeagueConfig,
): Map<PlayerId, Projection> {
  const out = new Map<PlayerId, Projection>();
  for (const p of players) {
    const raw = projectedStatLine(p.position, p.position_rank);
    const line = expandStatLine(league.scoring_settings, raw);
    out.set(p.player_key, {
      source_id: CURVE_SOURCE_ID,
      player_key: p.player_key,
      stat_line: raw,
      projected_points_league_scoring: scoreStatLine(league.scoring_settings, line),
      estimated: true,
    });
  }
  return out;
}

/**
 * Mean R² of the fitted curves a position actually uses, weighted by each
 * stat's contribution to that position's points under the league's scoring.
 * The UI uses this to label a position's estimates honestly rather than giving
 * every number the same apparent confidence.
 */
export function curveConfidence(position: Position, scoring: Record<string, number>): number {
  const curve = POSITION_CURVES[position];
  if (!curve) return 0;
  const topLine = projectedStatLine(position, 1);

  let weighted = 0;
  let total = 0;
  for (const [key, value] of Object.entries(topLine)) {
    const points = Math.abs((scoring[key] ?? 0) * value);
    if (points <= 0) continue;
    const stat = curve.stats[key];
    if (!stat) continue;
    weighted += points * stat.r2;
    total += points;
  }
  return total === 0 ? 0 : weighted / total;
}
